/**
 * Custom Hook for App Configuration
 * 
 * This hook provides app-specific configuration based on URL query parameters.
 * Follows SOLID principles:
 * - Single Responsibility: Only handles app config retrieval
 * - Dependency Inversion: Components depend on this hook, not directly on config
 * 
 * Usage:
 * const appConfig = useAppConfig();
 * 
 * Then access: appConfig.name, appConfig.galleryWallpapers, etc.
 */

import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getAppWebConfig, type AppWebConfig } from '../config/apps.config';

export function useAppConfig(): AppWebConfig {
  const [searchParams] = useSearchParams();
  
  const appConfig = useMemo(() => {
    const appId = searchParams.get('app');
    const config = getAppWebConfig(appId);
    
    // If no config found, return default (instawall)
    if (!config) {
      return getAppWebConfig('instawall')!;
    }
    
    return config;
  }, [searchParams]);
  
  return appConfig;
}
